import React, { useMemo } from 'react';
import './SavedUserTemplates.less';
import { Button, Card, Empty, Icon, Table } from 'antd';
import useGetSortedTemplates from '../hooks/useGetSortedTemplates';
import { useNavigate } from "react-router-dom";
import moment from 'moment';
import { useDeleteTemplateMutation } from '../service';

const { Column } = Table;

type TableRecord = {
    key: string;
    guid: string;
    updatedAt: number;
    name: string;
};

export default function SavedUserTemplates() {
    const navigate = useNavigate();

    const [deleteTemplate, { originalArgs, isLoading: isDeleting }] = useDeleteTemplateMutation();

    const { sortedTemplates } = useGetSortedTemplates({ includeDefault: false });

    const handleTemplateSelection = (guid: string) => {
        navigate(`/white-label/templates/${guid}`);
    };

    const handleDeleteTemplateClicked = (event: React.MouseEvent, guid: string) => {
        event.stopPropagation();
        deleteTemplate({ guid });
    };

    const handleCreateTemplateClicked = (event: React.MouseEvent<HTMLElement, MouseEvent>) => {
        event.currentTarget.blur();
        navigate('/white-label/templates/new');
    };

    const tableDataSource = useMemo(
        () =>
            sortedTemplates?.map((template, index) => ({
                key: index + 1,
                guid: template.guid,
                updatedAt: template.last_modified,
                name: template.name,
            })),
        [sortedTemplates]
    );

    const isDeletingTemplate = (guid: string) => isDeleting && guid === originalArgs?.guid;

    return (
        <div>
            <Card
                title={
                    <div className="ant-card-meta-detail">
                        <div className="ant-card-meta-title">Saved Templates</div>
                    </div>
                }
            >
                {sortedTemplates && sortedTemplates.length === 0 && (
                    <Empty
                        className="empty-state"
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                        description="You have no saved templates"
                    />
                )}

                {sortedTemplates && sortedTemplates.length > 0 && (
                    <div className="template-table-container custom-scrollbar">
                        <Table
                            className="templates-table"
                            dataSource={tableDataSource}
                            size="small"
                            pagination={false}
                            onRow={(record) => ({
                                onClick: () => handleTemplateSelection(record.guid),
                            })}
                        >
                            <Column
                                title="#"
                                dataIndex="key"
                                align="left"
                                width="5%"
                            />
                            <Column
                                className="no-wrap"
                                title="Updated at"
                                dataIndex="updatedAt"
                                width="20%"
                                align="left"
                                render={(_, record: TableRecord) => moment.unix(record.updatedAt).format('MMM DD, YYYY')}
                            />
                            <Column
                                className="no-wrap template-name"
                                title="Template name"
                                dataIndex="name"
                                align="left"
                                render={(_, record: TableRecord) => <span>{record.name}</span>}
                            />
                            <Column
                                className="no-wrap"
                                title=""
                                align="right"
                                render={(_, record: TableRecord) => (
                                    <span
                                        className={`remove-item ${isDeletingTemplate(record.guid) ? 'in-progress' : ''}`}
                                        onClick={(e) => handleDeleteTemplateClicked(e, record.guid)}
                                    >
                                        {isDeletingTemplate(record.guid) ? <Icon type="loading" /> : <Icon type="delete" />}
                                    </span>
                                )}
                            />
                        </Table>
                    </div>
                )}
            </Card>
            <Button
                className="new-template-btn"
                type="primary"
                onClick={handleCreateTemplateClicked}
            >
                Create new template
            </Button>
        </div>
    );
}
